/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isEqual from 'lodash.isequal';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useStatusBadgeColors, UseStatusBadgeColorsProps as StatusCellProps} from '../hooks/useStatusBadgeColors';

type LabelStyleParams = {
  textColor: string, color: string, isPadding: boolean, hasLongText: boolean
};

const useStyles = makeStyles(() => ({
  labelStyle : {
    color           : ({textColor}: LabelStyleParams) => textColor || '',
    backgroundColor : ({color}: LabelStyleParams) => color || '',
    padding         : ({isPadding, hasLongText}: LabelStyleParams) => (
      isPadding ? '0px 15px 0px 15px' : hasLongText ? '0px 34px' : '0px 8px 0px 8px'
    ),
    borderRadius   : '2px',
    lineHeight     : '23px',
    width          : '50%',
    display        : 'flex',
    justifyContent : 'center'
  }
}));

export const cellLabelStyles = (theme: DLSTheme) => ({
  labelContainer : {
    height         : '100%',
    width          : '100%',
    justifyContent : 'left',
    alignItems     : 'center',
    display        : 'flex',
    fontSize       : '10px',
    fontWeight     : 500
  }
});

export enum DefaultStatuses {
  COMPLETED = 'COMPLETED',
  SUCCESS = 'SUCCESS',
  VALID = 'VALID',
  FAILED = 'FAILED',
  INVALID = 'INVALID',
  IN_PROGRESS_CANCELLED = 'INPROGRESSCANCELLED',
  AT_RISK = 'AT RISK',
  PASS = 'PASS',
  NONE = 'NONE',
  ACTIVE = 'ACTIVE',
  COMPLIANT = 'COMPLIANT',
  PENDING = 'PENDING',
  NON_COMPLY = 'NON COMPLY'
};

/**
 * Re-usable cell renderer for rendering a colored badge based on the given status.
 *
 * The given status must also correspond to a given valid or invalid status that consumers
 * can customize. Please see usage examples below for further details.
 *
 * ```typescript
 *
 *    import {ColoredStatusCell} from '@ion/dls/src/grid/ColoredStatusCell';
 *
 *    // Default statuses for each state
 *    <ColoredStatusCell status={status} />
 *
 *    // Custom statuses for each state (positive = green, negative = red,
 *    // pending = orange, neutral = gray = other)
 *    <ColoredStatusCell status={status}
 *      positiveStatuses={[
 *        Status.COMPLETED,
 *        Status.COMPLETED_WITH_EXCEPTION
 *      ]}
 *      negativeStatuses={[
 *        Status.FAILED
 *      ]}
 *      pendingStatuses={[
 *        Status.CANCELLED
 *      ]} />
 *
 * ```
 */
export const ColoredStatusCell = ({
  status,
  display,
  isPadding = false,
  positiveStatuses = [DefaultStatuses.COMPLETED, DefaultStatuses.VALID, DefaultStatuses.SUCCESS],
  negativeStatuses = [DefaultStatuses.FAILED, DefaultStatuses.INVALID],
  pendingStatuses
}: StatusCellProps) => {
  const {color, textColor} = useStatusBadgeColors({
    status,
    positiveStatuses,
    negativeStatuses,
    pendingStatuses
  });

  const hasLongText = isEqual(status, DefaultStatuses.IN_PROGRESS_CANCELLED);
  const styles      = useStyles({textColor, color, isPadding, hasLongText});
  const classes     = useExtendStyles(() => styles, cellLabelStyles)();

  return status ? (
    <div className={classes.labelContainer}>
      <div className={styles.labelStyle}>{display ?? status}</div>
    </div>
  ) : <div/>;
};

export default memo(ColoredStatusCell);
